/**
 * Analytics API Service
 * 
 * Service for connecting to the backend analytics endpoints implemented in PX-221.
 * Handles all analytics-related API calls for campaigns and content performance.
 */

import { createApiService, responseTransformers } from './apiUtils';

// Create specialized API service for analytics
const analyticsApiService = createApiService('Analytics', '/api/analytics');

/**
 * Transform backend analytics data to frontend format
 * @param {Object} backendData - Analytics data from backend
 * @returns {Object} Transformed analytics data for frontend
 */
const transformAnalyticsData = (backendData) => {
  if (!backendData) return null;

  const camelCased = responseTransformers.toCamelCase(backendData);
  
  return {
    overview: {
      totalPosts: camelCased.totalPosts || 0,
      totalImpressions: camelCased.totalImpressions || 0,
      totalEngagement: camelCased.totalEngagement || 0,
      totalClicks: camelCased.totalClicks || 0,
      totalReach: camelCased.totalReach || 0,
      totalFollowers: camelCased.totalFollowers || 0,
      avgEngagementRate: camelCased.avgEngagementRate || 0,
      totalRevenue: camelCased.totalRevenue || 0,
      conversions: camelCased.conversions || 0
    },
    previousPeriod: camelCased.previousPeriod ? {
      totalPosts: camelCased.previousPeriod.totalPosts || 0,
      totalImpressions: camelCased.previousPeriod.totalImpressions || 0,
      totalEngagement: camelCased.previousPeriod.totalEngagement || 0,
      totalClicks: camelCased.previousPeriod.totalClicks || 0,
      totalReach: camelCased.previousPeriod.totalReach || 0,
      totalFollowers: camelCased.previousPeriod.totalFollowers || 0,
      avgEngagementRate: camelCased.previousPeriod.avgEngagementRate || 0,
      totalRevenue: camelCased.previousPeriod.totalRevenue || 0,
      conversions: camelCased.previousPeriod.conversions || 0
    } : null,
    performanceTimeline: camelCased.performanceTimeline || [],
    platformBreakdown: camelCased.platformBreakdown || [],
    topPosts: camelCased.topPosts || [],
    audienceInsights: camelCased.audienceInsights || {
      demographics: { ageGroups: [], gender: [] },
      locations: [],
      devices: []
    },
    conversionFunnel: camelCased.conversionFunnel || []
  };
};

/**
 * Analytics API functions
 */
export const analyticsApi = {
  /**
   * Get analytics dashboard overview
   * @param {string} campaignId - Campaign ID (optional)
   * @returns {Promise} Dashboard analytics data
   */
  getDashboard: async (campaignId = null) => {
    console.log(`[Analytics API] Fetching dashboard analytics${campaignId ? ` for campaign: ${campaignId}` : ''}`);
    
    const response = await analyticsApiService.get('/dashboard/', {
      campaign: (campaignId && campaignId !== 'demo-campaign-2024') ? campaignId : undefined,
    });
    
    // Transform response and extract data from wrapper
    const wrappedResponse = responseTransformers.single(response, transformAnalyticsData);
    return wrappedResponse.data;
  },

  /**
   * Get performance metrics with filtering
   * @param {Object} options - Query options
   * @returns {Promise} Performance metrics
   */
  getMetrics: async (options = {}) => {
    console.log('[Analytics API] Fetching performance metrics:', options);
    
    const response = await analyticsApiService.get('/metrics/', {
      campaign: (options.campaignId && options.campaignId !== 'demo-campaign-2024') ? options.campaignId : undefined,
      time_range: options.timeRange,
      platform: options.platform,
      metrics: Array.isArray(options.metrics) ? options.metrics.join(',') : options.metrics,
    });
    
    return responseTransformers.single(response);
  },

  /**
   * Get content patterns and insights
   * @param {string} campaignId - Campaign ID (optional)
   * @returns {Promise} Content patterns
   */
  getPatterns: async (campaignId = null) => {
    console.log(`[Analytics API] Fetching content patterns${campaignId ? ` for campaign: ${campaignId}` : ''}`);
    
    const response = await analyticsApiService.get('/patterns/', {
      campaign: (campaignId && campaignId !== 'demo-campaign-2024') ? campaignId : undefined,
    });
    
    return responseTransformers.single(response, (data) => data.patterns || []);
  },

  /**
   * Get AI-powered insights
   * @param {string} campaignId - Campaign ID (optional)
   * @returns {Promise} Analytics insights
   */
  getInsights: async (campaignId = null) => {
    console.log(`[Analytics API] Fetching insights${campaignId ? ` for campaign: ${campaignId}` : ''}`);
    
    const response = await analyticsApiService.get('/insights/', {
      campaign: (campaignId && campaignId !== 'demo-campaign-2024') ? campaignId : undefined,
    });
    
    return responseTransformers.single(response, (data) => data.insights || []);
  },

  /**
   * Get cross-platform analytics comparison
   * @param {string} campaignId - Campaign ID (optional)
   * @returns {Promise} Cross-platform analytics
   */
  getCrossPlatformAnalytics: async (campaignId = null) => {
    console.log(`[Analytics API] Fetching cross-platform analytics${campaignId ? ` for campaign: ${campaignId}` : ''}`);
    
    const response = await analyticsApiService.get('/cross-platform/', {
      campaign: (campaignId && campaignId !== 'demo-campaign-2024') ? campaignId : undefined,
    });
    
    return responseTransformers.single(response);
  },

  /**
   * Get user analytics profile
   * @returns {Promise} User analytics profile
   */
  getUserProfile: async () => {
    console.log('[Analytics API] Fetching user analytics profile');
    
    const response = await analyticsApiService.get('/profile/');
    return responseTransformers.single(response);
  },

  /**
   * Update user analytics profile
   * @param {Object} profileData - Profile data to update
   * @returns {Promise} Updated profile
   */
  updateUserProfile: async (profileData) => {
    console.log('[Analytics API] Updating user analytics profile:', profileData);
    
    const response = await analyticsApiService.put('/profile/', profileData);
    return responseTransformers.single(response);
  },

  /**
   * Track analytics event
   * @param {Object} eventData - Event data to track
   * @returns {Promise} Event tracking result
   */
  trackEvent: async (eventData) => {
    console.log('[Analytics API] Tracking event:', eventData);
    
    try {
      const response = await analyticsApiService.post('/events/track/', eventData);
      return responseTransformers.single(response);
    } catch (error) {
      console.error('Failed to track analytics event:', error);
      // Don't throw for tracking events to avoid disrupting user experience
      return { success: false, error: error.message };
    }
  },

  /**
   * Get overview metrics for main dashboard
   * @param {Object} options - Query options
   * @returns {Promise} Overview analytics data
   */
  getOverviewMetrics: async (options = {}) => {
    console.log('[Analytics API] Fetching overview metrics:', options);
    
    const response = await analyticsApiService.get('/overview/', {
      time_range: options.timeRange || '30d',
      platform: options.platform,
      start_date: options.startDate,
      end_date: options.endDate
    });
    
    return responseTransformers.single(response, transformAnalyticsData);
  },

  /**
   * Get performance timeline data
   * @param {Object} options - Query options
   * @returns {Promise} Performance timeline data
   */
  getPerformanceTimeline: async (options = {}) => {
    console.log('[Analytics API] Fetching performance timeline:', options);
    
    const response = await analyticsApiService.get('/timeline/', {
      time_range: options.timeRange || '30d',
      platform: options.platform,
      start_date: options.startDate,
      end_date: options.endDate,
      granularity: options.granularity || 'day'
    });
    
    return responseTransformers.single(response, (data) => data.timeline || []);
  },

  /**
   * Get top performing posts
   * @param {Object} options - Query options
   * @returns {Promise} Top performing posts data
   */
  getTopPerformingPosts: async (options = {}) => {
    console.log('[Analytics API] Fetching top performing posts:', options);
    
    const response = await analyticsApiService.get('/top-posts/', {
      time_range: options.timeRange || '30d',
      platform: options.platform,
      limit: options.limit || 10,
      metric: options.metric || 'engagement_rate'
    });
    
    return responseTransformers.single(response, (data) => data.top_posts || []);
  },

  /**
   * Get platform breakdown data
   * @param {Object} options - Query options
   * @returns {Promise} Platform breakdown data
   */
  getPlatformBreakdown: async (options = {}) => {
    console.log('[Analytics API] Fetching platform breakdown:', options);
    
    const response = await analyticsApiService.get('/platforms/', {
      time_range: options.timeRange || '30d',
      include_inactive: options.includeInactive || false
    });
    
    return responseTransformers.single(response, (data) => data.platforms || []);
  },

  /**
   * Get engagement breakdown data
   * @param {Object} options - Query options
   * @returns {Promise} Engagement breakdown data
   */
  getEngagementBreakdown: async (options = {}) => {
    console.log('[Analytics API] Fetching engagement breakdown:', options);
    
    const response = await analyticsApiService.get('/engagement/', {
      time_range: options.timeRange || '30d',
      platform: options.platform
    });
    
    return responseTransformers.single(response, (data) => data.engagement || {});
  },

  /**
   * Export analytics data
   * @param {Object} options - Export options
   * @returns {Promise} Export result
   */
  exportData: async (options = {}) => {
    console.log('[Analytics API] Exporting analytics data:', options);
    
    const response = await analyticsApiService.post('/export/', {
      format: options.format || 'csv',
      time_range: options.timeRange || '30d',
      platform: options.platform,
      metrics: options.metrics || ['impressions', 'engagement', 'clicks', 'reach'],
      include_posts: options.includePosts || false
    });
    
    return responseTransformers.single(response);
  }
};

/**
 * Analytics tracking helper function
 * @param {string} eventName - Event name
 * @param {Object} properties - Event properties
 */
export const trackAnalyticsEvent = (eventName, properties = {}) => {
  if (process.env.NODE_ENV === 'development') {
    console.log(`[Analytics Tracking] ${eventName}:`, properties);
  }
  
  // Track event asynchronously
  analyticsApi.trackEvent({
    event_type: eventName,
    event_data: properties,
    timestamp: new Date().toISOString()
  }).catch(error => {
    console.warn('Analytics tracking failed:', error);
  });
};

export default analyticsApi; 